import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  assignRoom,
  createBuilding,
  createRoom,
  evictOccupancy,
  listBuildings,
  listIncidents,
  listOccupancies,
  listRooms,
  registerDormPayment,
  updateBuilding,
  updateRoom,
} from 'service/DormService'
import { listStudents } from 'service/StudentsService'
import Notification from 'utils/Notifications'

import DormTabs from './components/DormTabs/DormTabs'
import RoomsTab from './components/RoomsTab/RoomsTab'
import BuildingsTab from './components/BuildingsTab/BuildingsTab'
import OccupanciesTab from './components/OccupanciesTab/OccupanciesTab'
import IncidentsTab from './components/IncidentsTab/IncidentsTab'
import {
  BuildingEditModal,
  RoomEditModal,
  AssignModal,
  PayModal,
} from './components/DormModals/DormModals'
import s from './DormPage.module.scss'

export default function DormPage() {
  const { t } = useTranslation('common')
  const [tab, setTab] = useState('rooms')
  const [buildings, setBuildings] = useState([])
  const [rooms, setRooms] = useState([])
  const [occupancies, setOccupancies] = useState([])
  const [incidents, setIncidents] = useState([])
  const [students, setStudents] = useState([])
  const [filterBuilding, setFilterBuilding] = useState('')
  const [editingRoom, setEditingRoom] = useState(null)
  const [editingBuilding, setEditingBuilding] = useState(null)
  const [assigning, setAssigning] = useState(null)
  const [paying, setPaying] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false })

  const reload = () => {
    listBuildings().then((r) => setBuildings(r.data?.results || r.data || []))
    listRooms({ building: filterBuilding }).then((r) => setRooms(r.data?.results || r.data || []))
    listOccupancies({ active: '1' }).then((r) => setOccupancies(r.data?.results || r.data || []))
    listIncidents().then((r) => setIncidents(r.data?.results || r.data || []))
  }
  useEffect(() => {
    reload()
    listStudents({ page_size: 200 }).then((r) => setStudents(r.data?.results || r.data || []))
    // eslint-disable-next-line
  }, [filterBuilding])

  const saveBuilding = async () => {
    try {
      if (editingBuilding.id) await updateBuilding(editingBuilding.id, editingBuilding)
      else await createBuilding(editingBuilding)
      setEditingBuilding(null)
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: t('common.error'), type: 'error' })
    }
  }
  const saveRoom = async () => {
    try {
      if (editingRoom.id) await updateRoom(editingRoom.id, editingRoom)
      else await createRoom(editingRoom)
      setEditingRoom(null)
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: t('common.error'), type: 'error' })
    }
  }
  const assign = async () => {
    try {
      await assignRoom({
        room: assigning.room,
        student: assigning.student,
        monthly_payment: assigning.monthly_payment,
        contract_no: assigning.contract_no || '',
        deposit: assigning.deposit || 0,
      })
      setNotify({ isOpen: true, message: t('dorm.settled'), type: 'success' })
      setAssigning(null)
      reload()
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || t('common.error'), type: 'error' })
    }
  }
  const evictOne = async (id) => {
    if (!window.confirm(t('dorm.evict_confirm'))) return
    await evictOccupancy(id, { reason: t('dorm.evict_reason') })
    reload()
  }
  const pay = async () => {
    try {
      await registerDormPayment({
        occupancy: paying.occupancy,
        period_year: paying.period_year,
        period_month: paying.period_month,
        amount: paying.amount,
        receipt_no: paying.receipt_no || '',
      })
      setNotify({ isOpen: true, message: t('dorm.payment_recorded'), type: 'success' })
      setPaying(null)
    } catch (e) {
      setNotify({ isOpen: true, message: t('common.error'), type: 'error' })
    }
  }

  return (
    <div className={s.page}>
      <DormTabs tab={tab} setTab={setTab} />

      {tab === 'rooms' && (
        <RoomsTab
          buildings={buildings}
          rooms={rooms}
          filterBuilding={filterBuilding}
          setFilterBuilding={setFilterBuilding}
          setEditingRoom={setEditingRoom}
          setAssigning={setAssigning}
        />
      )}

      {tab === 'buildings' && (
        <BuildingsTab buildings={buildings} setEditingBuilding={setEditingBuilding} />
      )}

      {tab === 'occupancies' && (
        <OccupanciesTab
          occupancies={occupancies}
          setPaying={setPaying}
          evictOne={evictOne}
        />
      )}

      {tab === 'incidents' && (
        <IncidentsTab incidents={incidents} reload={reload} />
      )}

      {editingBuilding && (
        <BuildingEditModal
          editingBuilding={editingBuilding}
          setEditingBuilding={setEditingBuilding}
          onSave={saveBuilding}
        />
      )}

      {editingRoom && (
        <RoomEditModal
          editingRoom={editingRoom}
          setEditingRoom={setEditingRoom}
          buildings={buildings}
          onSave={saveRoom}
        />
      )}

      {assigning && (
        <AssignModal
          assigning={assigning}
          setAssigning={setAssigning}
          students={students}
          onAssign={assign}
        />
      )}

      {paying && (
        <PayModal paying={paying} setPaying={setPaying} onPay={pay} />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}
